import { useState, useEffect, useMemo } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ClipboardCopy, Download, ArrowUp, ArrowDown, Printer, ListChecks, Save, Lock } from "lucide-react";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import { patchIssueState, createNegotiationSummary } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasFeature, tierLabel } from "@/lib/tier";
import { Link } from "react-router-dom";

/**
 * Negotiation Summary Builder.
 *
 * Modal-style builder that lets a Premium owner:
 *  - toggle which issues to include
 *  - reorder them (up/down)
 *  - choose to include seller questions / contractor questions / repair exposure
 *  - copy the formatted summary to clipboard
 *  - print / save to PDF via browser print dialog (cost-safe, no AI, no backend)
 *
 * Persistence: each include-toggle is mirrored to report_user_state via
 * patchIssueState({ added_to_negotiation_summary }) so the selection survives
 * reloads. Order is local-only (per session) — order persistence would require
 * a new DB field; deliberately deferred.
 */
function buildMarkdown({ report, data, included, order, opts }) {
    const lines = [];
    const addr = data.property_address || "Inspection property";
    lines.push(`# Negotiation Summary — ${addr}`);
    if (data.inspection_date) lines.push(`*Inspection date: ${data.inspection_date}*`);
    lines.push("");

    const cs = data.closing_strategy || {};
    if (cs.deal_risk_snapshot?.headline) {
        lines.push(`> ${cs.deal_risk_snapshot.headline}`);
        lines.push("");
    }

    // Issues
    if (included.length > 0) {
        lines.push("## Issues to negotiate");
        lines.push("");
        order.forEach((id, i) => {
            const iss = (data.issues || []).find((x) => x.issue_id === id);
            if (!iss || !included.includes(id)) return;
            lines.push(`### ${i + 1}. ${iss.title}`);
            lines.push(`*${iss.category}${iss.severity ? ` · ${iss.severity} priority` : ""}*`);
            if (iss.simplified_explanation) lines.push("");
            if (iss.simplified_explanation) lines.push(iss.simplified_explanation);
            if (iss.why_it_matters) {
                lines.push("");
                lines.push(`**Why it matters:** ${iss.why_it_matters}`);
            }
            if (opts.includeExposure && iss.cost_exposure?.range_text) {
                lines.push("");
                lines.push(`**Estimated exposure:** ${iss.cost_exposure.range_text}`);
            }
            if (opts.includeNextSteps && iss.recommended_next_step) {
                lines.push("");
                lines.push(`**Recommended next step:** ${iss.recommended_next_step}`);
            }
            if (iss.negotiation_relevance) {
                lines.push("");
                lines.push(`**Negotiation angle:** ${iss.negotiation_relevance}`);
            }
            lines.push("");
        });
    }

    if (opts.includeSellerQ && (cs.questions_for_seller || []).length > 0) {
        lines.push("## Questions for the seller");
        cs.questions_for_seller.forEach((q) => lines.push(`- ${q}`));
        lines.push("");
    }
    if (opts.includeContractorQ && (cs.questions_for_contractor || []).length > 0) {
        lines.push("## Questions for the contractor");
        cs.questions_for_contractor.forEach((q) => lines.push(`- ${q}`));
        lines.push("");
    }

    lines.push("---");
    lines.push("*Built with Buyer's Leverage — buyersleverage.com*");
    return lines.join("\n");
}

export default function NegotiationSummaryBuilder({ open, onClose, report, stateByIssue, onStateChange }) {
    const { user } = useAuth();
    const canSave = hasFeature(user, "saved_negotiation_summaries");
    const [saving, setSaving] = useState(false);
    const data = report?.full_summary;
    const allIssues = useMemo(() => data?.issues || [], [data]);
    const reportId = report?.report_id;

    // Initial selection logic:
    //  - If any issue has had its `added_to_negotiation_summary` explicitly
    //    persisted (true OR false), respect those stored values verbatim and
    //    default missing entries to "included".
    //  - Otherwise (no prior interaction at all), include everything.
    const initialSelected = useMemo(() => {
        const anyTouched = allIssues.some(
            (i) => typeof stateByIssue?.[i.issue_id]?.added_to_negotiation_summary === "boolean",
        );
        if (!anyTouched) {
            return allIssues.map((i) => i.issue_id);
        }
        return allIssues
            .filter((i) => {
                const v = stateByIssue?.[i.issue_id]?.added_to_negotiation_summary;
                // explicit false → exclude. true OR missing → include.
                return v !== false;
            })
            .map((i) => i.issue_id);
    }, [allIssues, stateByIssue]);

    const [order, setOrder] = useState(() => allIssues.map((i) => i.issue_id));
    const [included, setIncluded] = useState(initialSelected);
    const [opts, setOpts] = useState({
        includeSellerQ: true,
        includeContractorQ: true,
        includeExposure: true,
        includeNextSteps: true,
    });

    useEffect(() => {
        // Re-sync if the report changes.
        setOrder(allIssues.map((i) => i.issue_id));
        setIncluded(initialSelected);
    }, [allIssues, initialSelected]);

    if (!open || !data) return null;

    const toggle = async (id) => {
        const nowIncluded = !included.includes(id);
        setIncluded((prev) =>
            nowIncluded ? [...prev, id] : prev.filter((x) => x !== id),
        );
        // Persist (best-effort) so subsequent opens remember the selection.
        try {
            await patchIssueState(reportId, id, { added_to_negotiation_summary: nowIncluded });
            onStateChange?.(id, { added_to_negotiation_summary: nowIncluded });
        } catch {
            // non-fatal — local state is the source of truth in the modal
        }
    };

    const move = (id, dir) => {
        setOrder((prev) => {
            const i = prev.indexOf(id);
            if (i === -1) return prev;
            const ni = i + dir;
            if (ni < 0 || ni >= prev.length) return prev;
            const next = prev.slice();
            [next[i], next[ni]] = [next[ni], next[i]];
            return next;
        });
    };

    const markdown = buildMarkdown({ report, data, included, order, opts });

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(markdown);
            toast.success("Negotiation summary copied to clipboard.");
            track("negotiation_summary_copied", {
                report_id: reportId,
                included_count: included.length,
            });
        } catch {
            toast.error("Couldn't copy. Try the download button instead.");
        }
    };

    const handleDownload = () => {
        const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `negotiation-summary-${(reportId || "report").slice(-8)}.md`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        track("negotiation_summary_downloaded", {
            report_id: reportId,
            included_count: included.length,
        });
    };

    const handleSave = async () => {
        if (!canSave) {
            toast.error("Saved briefs require Investor Pro.");
            return;
        }
        if (!included.length) {
            toast.error("Add at least one issue to the brief.");
            return;
        }
        setSaving(true);
        try {
            const title = (report?.property_address
                ? `Brief — ${report.property_address}`
                : "Negotiation brief"
            ).slice(0, 200);
            await createNegotiationSummary({
                report_id: reportId,
                title,
                markdown,
                included_issue_ids: included,
                include_flags: opts,
            });
            track("negotiation_summary_saved", {
                report_id: reportId,
                included_count: included.length,
            });
            toast.success("Brief saved to your account.");
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Couldn't save brief.");
        } finally {
            setSaving(false);
        }
    };

    const handlePrint = () => {
        track("negotiation_summary_printed", { report_id: reportId });
        // Open a print-ready window with simple typography.
        const w = window.open("", "_blank", "width=900,height=700");
        if (!w) {
            toast.error("Pop-up blocked. Allow pop-ups to print.");
            return;
        }
        const html = markdownToHtml(markdown);
        w.document.write(`<!doctype html><html><head><title>Negotiation Summary</title><meta charset="utf-8"/>
            <style>
                body{font:14px/1.6 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#1F2428;max-width:760px;margin:32px auto;padding:0 24px;}
                h1{font:600 24px/1.2 Georgia,serif;margin:0 0 8px;color:#0B1B1E;}
                h2{font:600 17px/1.3 Georgia,serif;margin:28px 0 8px;color:#1F6E73;}
                h3{font:600 15px/1.3 Georgia,serif;margin:18px 0 4px;}
                p{margin:6px 0;} ul{margin:6px 0;padding-left:20px;} li{margin:4px 0;}
                em{color:#66707A;} blockquote{border-left:3px solid #1F6E73;padding:6px 12px;margin:14px 0;color:#1F6E73;background:#F0F5F5;}
                hr{border:0;border-top:1px solid #E5E0D8;margin:24px 0;}
                strong{color:#0B1B1E;}
            </style></head><body>${html}</body></html>`);
        w.document.close();
        setTimeout(() => w.print(), 500);
    };

    return (
        <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
            <SheetContent
                side="right"
                className="w-full sm:max-w-2xl overflow-y-auto bg-bl-paper"
                data-testid="negotiation-summary-builder"
            >
                <SheetHeader>
                    <SheetTitle className="font-display font-semibold text-bl text-xl flex items-center gap-2">
                        <ListChecks className="w-4 h-4 text-bl-primary" />
                        Negotiation Summary Builder
                    </SheetTitle>
                    <SheetDescription className="text-bl-muted text-sm leading-relaxed">
                        Pick which findings to include, reorder them, then copy
                        or print a clean brief to share with your agent or
                        attorney. Built entirely from your stored report — no
                        re-analysis, no extra cost.
                    </SheetDescription>
                </SheetHeader>

                {/* Include options */}
                <div className="mt-5 space-y-2 text-sm">
                    {[
                        ["includeExposure", "Include repair exposure ranges"],
                        ["includeNextSteps", "Include recommended next steps"],
                        ["includeSellerQ", "Include questions for the seller"],
                        ["includeContractorQ", "Include questions for the contractor"],
                    ].map(([k, label]) => (
                        <label
                            key={k}
                            className="flex items-center gap-2 cursor-pointer text-bl-muted hover:text-bl"
                        >
                            <input
                                type="checkbox"
                                checked={opts[k]}
                                onChange={() => setOpts((o) => ({ ...o, [k]: !o[k] }))}
                                data-testid={`negsum-opt-${k}`}
                            />
                            {label}
                        </label>
                    ))}
                </div>

                {/* Issue picker */}
                <div className="mt-6">
                    <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium mb-2">
                        Issues ({included.length}/{allIssues.length} included)
                    </div>
                    <ul className="space-y-2">
                        {order.map((id) => {
                            const iss = allIssues.find((x) => x.issue_id === id);
                            if (!iss) return null;
                            const isIncluded = included.includes(id);
                            return (
                                <li
                                    key={id}
                                    className={
                                        "bl-card p-3 flex items-center gap-3 " +
                                        (isIncluded ? "" : "opacity-60")
                                    }
                                    data-testid={`negsum-row-${id}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isIncluded}
                                        onChange={() => toggle(id)}
                                        className="flex-shrink-0"
                                        data-testid={`negsum-toggle-${id}`}
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-bl truncate">
                                            {iss.title}
                                        </div>
                                        <div className="text-[10px] tracking-[0.16em] uppercase text-bl-muted">
                                            {iss.category} · {iss.severity}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => move(id, -1)}
                                        className="text-bl-muted hover:text-bl p-1"
                                        aria-label="Move up"
                                        data-testid={`negsum-up-${id}`}
                                    >
                                        <ArrowUp className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => move(id, 1)}
                                        className="text-bl-muted hover:text-bl p-1"
                                        aria-label="Move down"
                                        data-testid={`negsum-down-${id}`}
                                    >
                                        <ArrowDown className="w-3.5 h-3.5" />
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>

                {/* Preview */}
                <details className="mt-6 bl-card p-4" data-testid="negsum-preview">
                    <summary className="cursor-pointer text-bl-primary text-sm font-medium">
                        Preview output
                    </summary>
                    <pre className="mt-3 whitespace-pre-wrap text-xs text-bl leading-relaxed font-mono-bl max-h-72 overflow-y-auto">
                        {markdown}
                    </pre>
                </details>

                {/* Actions */}
                <div className="mt-6 flex flex-wrap gap-2 sticky bottom-0 bg-bl-paper pt-4 border-t border-bl">
                    <button
                        type="button"
                        onClick={handleCopy}
                        className="bl-btn-primary text-sm"
                        data-testid="negsum-copy"
                    >
                        <ClipboardCopy className="w-3.5 h-3.5" /> Copy as Markdown
                    </button>
                    <button
                        type="button"
                        onClick={handleDownload}
                        className="bl-btn-secondary text-sm"
                        data-testid="negsum-download"
                    >
                        <Download className="w-3.5 h-3.5" /> Download .md
                    </button>
                    <button
                        type="button"
                        onClick={handlePrint}
                        className="bl-btn-secondary text-sm"
                        data-testid="negsum-print"
                    >
                        <Printer className="w-3.5 h-3.5" /> Print / Save PDF
                    </button>
                    {canSave ? (
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving || !included.length}
                            className="bl-btn-secondary text-sm"
                            data-testid="negsum-save"
                        >
                            <Save className="w-3.5 h-3.5" />
                            {saving ? "Saving…" : "Save brief"}
                        </button>
                    ) : (
                        <Link
                            to="/pricing"
                            className="bl-btn-secondary text-sm opacity-90"
                            data-testid="negsum-save-locked"
                            title={`Save briefs requires ${tierLabel("investor_pro")}`}
                        >
                            <Lock className="w-3.5 h-3.5" /> Save brief · {tierLabel("investor_pro")}
                        </Link>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}

// Tiny markdown → HTML for the print window. Handles only what buildMarkdown
// emits (headings, blockquotes, bullets, bold, italic, hr). Not a general parser.
function markdownToHtml(md) {
    const lines = md.split("\n");
    let out = "";
    let inList = false;
    const close = () => {
        if (inList) {
            out += "</ul>";
            inList = false;
        }
    };
    for (const raw of lines) {
        const l = raw;
        if (l.startsWith("# ")) {
            close();
            out += `<h1>${inline(l.slice(2))}</h1>`;
        } else if (l.startsWith("## ")) {
            close();
            out += `<h2>${inline(l.slice(3))}</h2>`;
        } else if (l.startsWith("### ")) {
            close();
            out += `<h3>${inline(l.slice(4))}</h3>`;
        } else if (l.startsWith("> ")) {
            close();
            out += `<blockquote>${inline(l.slice(2))}</blockquote>`;
        } else if (l.startsWith("- ")) {
            if (!inList) {
                out += "<ul>";
                inList = true;
            }
            out += `<li>${inline(l.slice(2))}</li>`;
        } else if (l.trim() === "---") {
            close();
            out += "<hr/>";
        } else if (l.trim() === "") {
            close();
            out += "";
        } else {
            close();
            out += `<p>${inline(l)}</p>`;
        }
    }
    close();
    return out;
}

function inline(s) {
    return s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\*(.+?)\*/g, "<em>$1</em>");
}
